/**
 * RulesFilterPopover Component
 * 
 * Filter controls specific to the Rules tab.
 * Renders inside a popover triggered by the filter button.
 */

import React from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Switch,
  Typography,
} from '@mui/material';

import type { RuleScope, RuleType } from '@features/rules/types';

import { DEFAULT_RULES_FILTERS, type RulesFilters } from './types';

interface RulesFilterPopoverProps {
  filters: RulesFilters;
  onFiltersChange: (filters: RulesFilters) => void;
  onClose: () => void;
}

const SCOPE_OPTIONS: { value: RuleScope; label: string }[] = [
  { value: 'both', label: 'All Rules' },
  { value: 'global', label: 'Global Rules Only' },
  { value: 'org', label: 'Organization Rules Only' },
];

const RULE_TYPE_OPTIONS: { value: RuleType | 'all'; label: string }[] = [
  { value: 'all', label: 'All Types' },
  { value: 'compliance', label: 'Compliance Rules' },
  { value: 'learning', label: 'Learning Rules' },
];

export const RulesFilterPopover: React.FC<RulesFilterPopoverProps> = ({
  filters,
  onFiltersChange,
  onClose,
}) => {
  const handleScopeChange = (event: SelectChangeEvent) => {
    onFiltersChange({
      ...filters,
      scope: event.target.value as RuleScope,
    });
  };

  const handleRuleTypeChange = (event: SelectChangeEvent) => {
    onFiltersChange({
      ...filters,
      ruleType: event.target.value as RuleType | 'all',
    });
  };

  const handleShowDisabledChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      showDisabled: event.target.checked,
    });
  };

  const handleReset = () => {
    onFiltersChange(DEFAULT_RULES_FILTERS);
  };

  // Check if any filters are active (different from defaults)
  const hasActiveFilters =
    filters.scope !== DEFAULT_RULES_FILTERS.scope ||
    filters.ruleType !== DEFAULT_RULES_FILTERS.ruleType ||
    filters.showDisabled !== DEFAULT_RULES_FILTERS.showDisabled;

  return (
    <Box sx={{ p: 2.5, minWidth: 280 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Filter Rules
        </Typography>
        {hasActiveFilters && (
          <Button size="small" onClick={handleReset} sx={{ textTransform: 'none' }}>
            Reset
          </Button>
        )}
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Scope Filter */}
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel id="scope-filter-label">Scope</InputLabel>
        <Select
          labelId="scope-filter-label"
          value={filters.scope}
          label="Scope"
          onChange={handleScopeChange}
        >
          {SCOPE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Rule Type Filter */}
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel id="rule-type-filter-label">Rule Type</InputLabel>
        <Select
          labelId="rule-type-filter-label"
          value={filters.ruleType}
          label="Rule Type"
          onChange={handleRuleTypeChange}
        >
          {RULE_TYPE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Show Disabled Toggle */}
      <FormControlLabel
        control={
          <Switch
            checked={filters.showDisabled}
            onChange={handleShowDisabledChange}
            size="small"
          />
        }
        label={
          <Typography variant="body2" color="text.secondary">
            Show disabled rules
          </Typography>
        }
        sx={{ ml: 0 }}
      />

      <Divider sx={{ my: 2 }} />

      <Button
        variant="contained"
        fullWidth
        onClick={onClose}
        sx={{ textTransform: 'none' }}
      >
        Apply Filters
      </Button>
    </Box>
  );
};

